import {
  computed,
  makeObservable,
  observable,
  override,
  runInAction,
} from 'mobx'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import { PromptType } from '../firestore/PromptVersion/types'
import { AdminPromptCubit } from './AdminPromptCubit'
import {
  PostRoomStatePromptVersion,
  RubricPromptVersion,
} from '../models/PromptVersion'
import { PromptVersionStatus, type StaticModelCollection } from '../types'
import { noTry } from '../util'
import {
  copyPromptVersion,
  deletePromptVersion,
  type PromptKey,
  updatePromptVersionConfidenceThreshold,
  updatePromptVersionPrompt,
  updatePromptVersionStatus,
} from '../firestore/PromptVersion'
import { installNewPromptVersion } from '../firestore/CurrentPromptVersion'
import { PromptPostRoomStateAIEvent } from '../models/PromptPostRoomStateAIEvent'
import {
  fetchResultsForSuccessfulPostRoomStatePromptTest,
  getPromptPostRoomStateAIEvents,
  getResultsForPostRoomStatePromptTests,
  startPostRoomStatePromptTestForRoom,
} from '../firestore/PromptPostRoomStateAIEvent'
import type {
  DocumentReference,
  QueryDocumentSnapshot,
  QuerySnapshot,
  Timestamp,
} from 'firebase/firestore'
import {
  collection,
  runTransaction,
  type DocumentData,
} from 'firebase/firestore'
import { RoomState } from '../models/RoomState'
import {
  fetchMostRecentlyGradedRoomStatesForSlideDeck,
  fetchRoomsForSectionWhereAISucceeded,
  fetchRoomState,
} from '../firestore/RoomState'
import type { SlideDeck } from '../models/SlideDeck'
import { fetchSlideDeck } from '../firestore/SlideDeck'
import { getDocsWithError, getDocWithError } from '../firestore-mobx/fetch'
import { fetchSectionAssignment } from '../firestore/SectionAssignment'
import { SectionAssignment } from '../models/SectionAssignment'
import { fetchSection } from '../firestore/Section'
import { Section } from '../models/Section'
import { convertDocumentSnapshotToModel } from '../firestore-mobx/stream'
import { fetchRoomStateEngagementForRoomState } from '../firestore/RoomStateEngagement'
import { type StreamInterface } from 'tricklejs/dist/types'

type Tabs =
  | 'primaryPrompt'
  | 'outputPrompt'
  | 'statsPrompt'
  | 'reminderPrompt'
  | 'details'
  | 'logs'
  | 'test'

export class AdminPromptVersionCubit extends AdminPromptCubit<Tabs> {
  versionId: string

  @observable private _handlebars: Awaited<
    ReturnType<typeof this._importHandlebars>
  > | null = null

  _postRoomStateTests: StaticModelCollection<PromptPostRoomStateAIEvent>

  @observable _postRoomStateTestResults: StreamResult<
    ReturnType<typeof getResultsForPostRoomStatePromptTests>
  > | null = null

  @observable sortingStrategy:
    | {
        column:
          | 'slideDeck'
          | 'roomName'
          | 'className'
          | 'roomScheduledOrStartedAt'
        direction: 'asc' | 'desc'
      }
    | undefined

  testRooms = observable.map<string, RoomState>()

  testSlideDecks = observable.map<string, SlideDeck>()
  testSections = observable.map<string, Section>()

  constructor(
    repository: FirebaseRepository,
    params: {
      promptType: PromptType
      typeId: string
      versionId: string
      defaultTab: Tabs
    }
  ) {
    super(repository, params)
    makeObservable(this)
    this.versionId = params.versionId
    this._postRoomStateTests =
      PromptPostRoomStateAIEvent.emptyCollection(repository)
  }

  initialize() {
    super.initialize()
    this._importHandlebars().then((handlebars) => {
      runInAction(() => (this._handlebars = handlebars))
    })
  }

  updatePromptVersionPrompt = async (
    args: Pick<
      Parameters<typeof updatePromptVersionPrompt>[1],
      'field' | 'newPrompt'
    >
  ) => {
    return await updatePromptVersionPrompt(this.repository, {
      ...args,
      ...this.promptAndSubType,
      versionId: this.versionId,
    })
  }

  updatePromptConfidenceThreshold = async (
    args: Pick<
      Parameters<typeof updatePromptVersionConfidenceThreshold>[1],
      'confidenceThreshold'
    >
  ) => {
    return await updatePromptVersionConfidenceThreshold(this.repository, {
      versionId: this.versionId,
      ...args,
      ...this.promptAndSubType,
    })
  }

  /**
   * this check could be performed in tsx. only doing this to prevent
   * polluting the exports with a one off error type
   */
  isRoomNotCompletedError = (err: unknown) =>
    err instanceof RoomNotCompletedError

  /**
   * Recursively gets all documents and subcollections under a given document ref for ai test results
   */
  getTestResultsDocumentTree = async (docRef: DocumentReference) => {
    const possibleCollectionNames = ['ai_results', 'user_ai_results', 'result']

    const docSnap = await getDocWithError(docRef, 'GetDocumentTreeError')

    const data = docSnap.data() ?? {}

    const subcollections = (
      await Promise.all(
        possibleCollectionNames.map((colName) => {
          return (async () => {
            const colRef = collection(docRef, colName)
            const colSnap = await getDocsWithError(
              colRef,
              `GetDocumentTreeError: collection ${colName}`
            )
            if (colSnap.empty) return null
            return { colSnap, colName }
          })()
        })
      )
    ).filter((col) => col !== null) as {
      colSnap: QuerySnapshot<DocumentData, DocumentData>
      colName: string
    }[]

    if (subcollections.length > 0) {
      data._subcollections = {}
      for (const { colSnap, colName } of subcollections) {
        const colData: Record<string, unknown> = {}
        for (const queryDocSnap of colSnap.docs) {
          colData[queryDocSnap.id] = await this.getTestResultsDocumentTree(
            queryDocSnap.ref
          )
        }
        data._subcollections[colName] = colData
      }
    }

    return { data, id: docSnap.id }
  }

  /**
   * returns error if the handlebars syntax is invalid
   * this will **NOT** consider data schemas, only the handlebars syntax
   */
  validateHandlebarsSyntax(
    prompt: string,
    { checkJSON } = { checkJSON: false }
  ) {
    if (!this._handlebars) throw new Error('Handlebars not loaded')
    if (!checkJSON) {
      const [, error] = noTry(() => this._handlebars!.precompile(prompt))
      return { error }
    }
    const [template, error] = noTry(() => this._handlebars!.compile(prompt))
    if (error) return { error }
    // try parse json
    const [, error2] = noTry(() => JSON.parse(template({})))
    return { error2 }
  }

  private _importHandlebars = async () => {
    return await import('handlebars')
  }

  async installVersion(installNotes: string) {
    await installNewPromptVersion(this.repository, {
      previousPromptVersionId: this.currentVersion.data.promptVersionId,
      promptVersionId: this.versionId,
      promptType: this.promptAndSubType.promptType,
      subType: this.promptAndSubType.subType,
      installNotes,
    })
  }

  async deleteVersion() {
    return await deletePromptVersion(this.repository, {
      ...this.promptAndSubType,
      versionId: this.versionId,
    })
  }

  async publishVersion(publisherNotes: string) {
    return await updatePromptVersionStatus(this.repository, {
      versionId: this.versionId,
      status: PromptVersionStatus.published as const,
      publisherNotes,
      ...this.promptAndSubType,
    })
  }

  async copyVersion({
    versionNotes,
    versionName,
  }: {
    versionNotes: string
    versionName: string
  }) {
    return await copyPromptVersion(this.repository, {
      versionNotes,
      versionName,
      versionId: this.versionId,
      ...this.promptAndSubType,
    })
  }

  async fetchPostRoomStateTestResults({ roomId }: { roomId: string }) {
    const { promptType, subType } = this.promptAndSubType

    if (promptType !== PromptType.postRoomState)
      throw new Error(
        `getPostRoomStateTestResults is only available for PostRoomStatePromptType, current type is ${this.promptAndSubType.promptType}`
      )

    return await fetchResultsForSuccessfulPostRoomStatePromptTest(
      this.repository,
      {
        promptType: subType,
        promptVersionId: this.versionId,
        roomId,
      }
    )
  }

  async startPostRoomStateTestForRoom({ roomId }: { roomId: string }) {
    const { promptType, subType } = this.promptAndSubType

    if (promptType !== PromptType.postRoomState)
      throw new Error(
        `getPostRoomStateTestResults is only available for PostRoomStatePromptType, current type is ${this.promptAndSubType.promptType}`
      )

    return await startPostRoomStatePromptTestForRoom(this.repository, {
      promptType: subType,
      promptVersionId: this.versionId,
      roomId,
    })
  }

  @computed
  get postRoomStateTests() {
    const { promptType, subType } = this.promptAndSubType
    if (promptType !== PromptType.postRoomState)
      throw new Error(
        `postRoomStateTests is only available for PostRoomStatePromptType, current type is ${this.promptAndSubType.promptType}`
      )

    if (
      !('showTestingTab' in this.prompt.data) ||
      !this.prompt.data.showTestingTab
    )
      throw new Error(
        `Testing is not enabled for prompt type ${promptType}:${subType}`
      )

    const testsKey = 'post_room_state_tests'
    const resultsKey = 'post_room_state_results'
    if (!this.hasStream(testsKey)) {
      this.addStream(
        getPromptPostRoomStateAIEvents(this.repository, {
          promptType: subType,
          promptVersionId: this.versionId,
        }),
        (models) => {
          this._postRoomStateTests.replaceModels(models)

          models.forEach(({ data: { roomId } }) => {
            if (!this.testRooms.has(roomId))
              this.testRooms.set(roomId, RoomState.empty(this.repository))

            fetchRoomState(this.repository, { roomStateId: roomId }).then(
              (roomState) => {
                runInAction(() => {
                  this.testRooms.set(roomId, roomState)
                  const { slideDeckId, sectionId } = roomState.data
                  if (slideDeckId && !this.testSlideDecks.has(slideDeckId)) {
                    fetchSlideDeck(this.repository, {
                      slideDeckId,
                    }).then((slideDeck) => {
                      runInAction(() => {
                        this.testSlideDecks.set(slideDeckId, slideDeck)
                      })
                    })
                  }
                  if (sectionId && !this.testSections.has(sectionId)) {
                    fetchSection(this.repository, {
                      sectionId,
                    }).then((section) => {
                      runInAction(() => {
                        const { instructorUserId } = section.data
                        this.repository.userStore.getUser(instructorUserId)
                        this.testSections.set(sectionId, section)
                      })
                    })
                  }
                })
              }
            )
          })
        },
        { name: testsKey }
      )
    }

    if (!this.hasStream(resultsKey)) {
      this.addStream(
        getResultsForPostRoomStatePromptTests(this.repository, {
          promptType: subType,
          promptVersionId: this.versionId,
        }),
        (snap) => {
          runInAction(() => (this._postRoomStateTestResults = snap))
        },
        { name: resultsKey }
      )
    }

    const isLoading =
      this._postRoomStateTests.isLoading ||
      this._postRoomStateTestResults === null

    const emptyResultsMap = new Map<
      string,
      {
        doc: QueryDocumentSnapshot<DocumentData, DocumentData>
        data: { aIId: string; updatedAt: Timestamp; roomId: string }
      }[]
    >()

    const results =
      this._postRoomStateTestResults?.reduce((acc, doc) => {
        const data = doc.data()
        if (!data) return acc
        const curr = acc.get(data.roomId)
        if (!curr) acc.set(data.roomId, [{ doc, data }])
        else curr.push({ doc, data })
        return acc
      }, emptyResultsMap) ?? emptyResultsMap

    // sort the results by updatedAt in each map entry in place
    results.forEach((value) => {
      value.sort(
        (a, b) => a.data.updatedAt.toMillis() - b.data.updatedAt.toMillis()
      )
    })

    return {
      isLoading,
      data: this._postRoomStateTests.models.map((test) => {
        const resultsForRoom = results.get(test.data.roomId)
        const roomState = this.testRooms.get(test.data.roomId)
        const section = roomState
          ? this.testSections.get(roomState.data.sectionId ?? '')
          : null
        const slideDeck = this.testSlideDecks.get(
          roomState?.data.slideDeckId ?? ''
        )

        const resultsForTest = resultsForRoom
          ? this.extractResultsForTest(test, resultsForRoom)
          : null

        return {
          test,
          results: resultsForTest,
          roomState: roomState && roomState.isNotEmpty ? roomState : null,
          slideDeck: slideDeck && slideDeck.isNotEmpty ? slideDeck : null,
          section: section && section.isNotEmpty ? section : null,
          scheduledOrStartedAt:
            roomState?.data.scheduledAt ?? roomState?.data.roomStartedAt,

          // delete the ai test record as well as the results records for test with the given id
          deleteTest: async () => {
            if (!test.data.endedAt)
              throw new Error('can not delete test that is still running')

            // todo: reintroduce this once the backend is recording results for every AI marked success
            // if (
            //   test.data.success &&
            //   (!resultsForTest || !resultsForTest.length)
            // )
            //   throw new Error(
            //     'can not delete successful test without corresponding results'
            //   )

            // delete test and results in transaction, all or nothing to maintain history
            await runTransaction(this.repository.firestore, async (t) => {
              const testAndResults = [test, ...(resultsForTest ?? [])]
              testAndResults.forEach(({ doc }) => t.delete(doc.ref))
            })
          },
        }
      }),
    }
  }

  @computed
  get postRoomStateTestsGroupedByRoom() {
    if (this.postRoomStateTests.isLoading)
      return {
        isLoading: true,
        data: new Map<string, typeof this.postRoomStateTests.data>(),
      }

    // first sort by test startedAt
    const sortedTests = [...this.postRoomStateTests.data].sort((a, b) => {
      const [aStartedAt, bStartedAt] = [
        a.test.data.startedAt?.getTime() ?? 0,
        b.test.data.startedAt?.getTime() ?? 0,
      ]

      return bStartedAt - aStartedAt
    })

    // then group by roomId
    const groupedTests = sortedTests.reduce((acc, testData) => {
      const roomId = testData.test.data.roomId
      if (!acc.has(roomId)) acc.set(roomId, [testData])
      else acc.get(roomId)?.push(testData)
      return acc
    }, new Map<string, typeof this.postRoomStateTests.data>())

    return {
      isLoading: this.postRoomStateTests.isLoading,
      data: this.sortGroupedPostRoomStateTests(groupedTests),
    }
  }

  changePostRoomStateTestsSortingStrategy = (
    column: Exclude<typeof this.sortingStrategy, undefined>['column']
  ) => {
    const currentStrategy = this.sortingStrategy

    const setStrategy = (newStrategy: typeof this.sortingStrategy) =>
      runInAction(() => (this.sortingStrategy = newStrategy))

    // toggle back to undefined if the column is the same and direction is asc (second click)
    if (currentStrategy?.column === column) {
      if (currentStrategy.direction === 'desc') return setStrategy(undefined)
      return setStrategy({ column, direction: 'desc' })
    }

    // else, set to desc on column (new strategy)
    return setStrategy({ column, direction: 'asc' })
  }

  private sortGroupedPostRoomStateTests = (
    groupedTests: Map<string, typeof this.postRoomStateTests.data>
  ) => {
    return new Map<string, typeof this.postRoomStateTests.data>(
      [...groupedTests.entries()].sort(([, aTests], [, bTests]) => {
        const [a, b]: [number, number] | [string, string] = (() => {
          switch (this.sortingStrategy?.column) {
            case undefined:
              return [0, 0]
            case 'slideDeck':
              return [
                aTests[0]?.slideDeck?.data.slideDeckName ?? '',
                bTests[0]?.slideDeck?.data.slideDeckName ?? '',
              ]
            case 'roomName':
              return [
                aTests[0]?.roomState?.simpleRoomName ?? '',
                bTests[0]?.roomState?.simpleRoomName ?? '',
              ]
            case 'className':
              return [
                aTests[0]?.section?.data.className ?? '',
                bTests[0]?.section?.data.className ?? '',
              ]
            case 'roomScheduledOrStartedAt':
              return [
                aTests[0]?.scheduledOrStartedAt?.getTime() ?? 0,
                bTests[0]?.scheduledOrStartedAt?.getTime() ?? 0,
              ]
          }
        })()

        const first = this.sortingStrategy?.direction === 'asc' ? a : b
        const second = this.sortingStrategy?.direction === 'asc' ? b : a

        const comparison =
          typeof first === 'string'
            ? first
                .toLowerCase()
                .localeCompare((second as string).toLowerCase(), undefined, {
                  numeric: true,
                  sensitivity: 'base',
                })
            : first - (second as number)

        // if we have a sorting strategy that can be sorted on, use that
        if (comparison !== 0) return comparison

        // else, sort by the most recently started test in the group
        const aRecentStartedAt = Math.max(
          ...aTests.map((test) => test.test.data.startedAt?.getTime() ?? 0)
        )
        const bRecentStartedAt = Math.max(
          ...bTests.map((test) => test.test.data.startedAt?.getTime() ?? 0)
        )
        return bRecentStartedAt - aRecentStartedAt
      })
    )
  }

  getDataForSectionOrSlideDeck = async (
    params: { slideDeckId: string } | { sectionId: string }
  ) => {
    const rooms = await ('sectionId' in params
      ? (async () =>
          (
            await fetchRoomsForSectionWhereAISucceeded(this.repository, {
              sectionId: params.sectionId,
            })
          )
            // recent active slide changed at to top of list
            .map((doc) =>
              convertDocumentSnapshotToModel(this.repository, doc, RoomState)
            )
            .sort((a, b) => {
              const aChangedAt = a.data.activeSlideChangedAt?.getTime() ?? 0
              const bChangedAt = b.data.activeSlideChangedAt?.getTime() ?? 0
              return bChangedAt - aChangedAt
            })
            .slice(0, 25))() // limit to 25 rooms (todo: think about enforcing this at query level)
      : (async () =>
          await fetchMostRecentlyGradedRoomStatesForSlideDeck(this.repository, {
            slideDeckId: params.slideDeckId,
            limit: 25,
          }))())

    // {sectionId}/{assignmentId}
    const sectionAssignmentIds = new Set<string>()
    const sectionIds = new Set<string>()
    const slideDeckIds = new Set<string>()

    for (const room of rooms) {
      const { assignmentId, sectionId, slideDeckId } = room.data
      if (sectionId && assignmentId)
        sectionAssignmentIds.add(`${sectionId}/${assignmentId}`)
      if (sectionId) sectionIds.add(sectionId)
      if (slideDeckId) slideDeckIds.add(slideDeckId)
    }

    const [sections, assignments, slideDecks] = await Promise.all([
      await Promise.all(
        Array.from(sectionIds).map((id) =>
          fetchSection(this.repository, { sectionId: id })
        )
      ),
      await Promise.all(
        Array.from(sectionAssignmentIds).map((id) => {
          const [sectionId, assignmentId] = id.split('/')
          return fetchSectionAssignment(this.repository, {
            sectionId,
            assignmentId,
          })
        })
      ),
      await Promise.all(
        Array.from(slideDeckIds).map((id) =>
          fetchSlideDeck(this.repository, { slideDeckId: id })
        )
      ),
    ])

    return rooms.map((roomState) => {
      const slideDeck = slideDecks.find(
        (slideDeck) => slideDeck.id === roomState.data.slideDeckId
      )
      const section = sections.find(
        (section) => section.id === roomState.data.sectionId
      )
      const assignment = assignments.find((assignment) => {
        return (
          assignment.id === roomState.data.assignmentId &&
          assignment.data.sectionId === roomState.data.sectionId
        )
      })

      return {
        roomState,
        slideDeck,
        section,
        assignment,
      }
    })
  }

  getDataForRoom = async (roomId: string) => {
    const roomState = await fetchRoomState(this.repository, {
      roomStateId: roomId,
    })

    const { slideDeckId, assignmentId, sectionId } = roomState.data

    const engagementReq = fetchRoomStateEngagementForRoomState(
      this.repository,
      { roomId }
    )
    const slideDeckReq = fetchSlideDeck(this.repository, { slideDeckId })
    const sectionReq = sectionId
      ? fetchSection(this.repository, {
          sectionId,
        })
      : Promise.resolve(Section.empty(this.repository))
    const assignmentReq =
      assignmentId && sectionId
        ? fetchSectionAssignment(this.repository, {
            sectionId,
            assignmentId,
          })
        : Promise.resolve(SectionAssignment.empty(this.repository))

    const [engagement, slideDeck, section, assignment] = await Promise.all([
      engagementReq,
      slideDeckReq,
      sectionReq,
      assignmentReq,
    ])

    if (!engagement.length) throw new RoomNotCompletedError(roomId)

    return {
      roomState,
      slideDeck,
      section,
      assignment,
    }
  }

  private extractResultsForTest(
    test: PromptPostRoomStateAIEvent,
    results: {
      doc: QueryDocumentSnapshot<DocumentData, DocumentData>
      data: {
        aIId: string
        roomId: string
        updatedAt: Timestamp
        systemPrompt?: string
        confidenceScore?: number
      }
    }[]
  ) {
    // results are sorted by updated
    const { startedAt, endedAt, success } = test.data

    // no results or fail or not finished
    if (!success || !startedAt || !endedAt) return null

    return results.filter((result) => result.data.aIId === test.id)
  }

  @computed
  get isInstalledVersion() {
    return this.currentVersion.data.promptVersionId === this.versionId
  }

  @computed
  get installedVersion() {
    const found = this.versions.models.find(
      (version) => version.id === this.currentVersion.data.promptVersionId
    )

    if (!found && this.cubitReady)
      throw new Error(
        `Installed version with id ${this.currentVersion.data.promptVersionId} not found`
      )

    if (found) return found

    switch (this.promptAndSubType.promptType) {
      case PromptType.rubric:
        return RubricPromptVersion.empty(this.repository)
      case PromptType.postRoomState:
        return PostRoomStatePromptVersion.empty(this.repository)
      case PromptType.interactiveElement:
        return PostRoomStatePromptVersion.empty(this.repository)
    }
  }

  @computed
  get currentPromptEditDisabled() {
    const outputKey: PromptKey = 'outputPrompt'

    // can't edit currently installed
    if (
      this.isInstalledVersion ||
      this.version.data.versionStatus !== PromptVersionStatus.draft
    )
      return true

    if (this.tab === outputKey) {
      const canEdit = this.repository.breakoutUser?.canEditOutputPrompt ?? false
      return !canEdit
    }

    return false
  }

  @computed
  get cubitReady() {
    return super.ready && this._handlebars !== null
  }

  @override
  /** @deprecated -- call cubitReady() instead */
  get ready() {
    throw new Error('call cubitReady() instead') // can't override base computed ready, mobx constraint
    return false as never
  }

  @computed
  get version() {
    // return empty is loading
    if (this.versions.isLoading) {
      switch (this.promptAndSubType.promptType) {
        case PromptType.rubric:
          return RubricPromptVersion.empty(this.repository)
        case PromptType.postRoomState:
          return PostRoomStatePromptVersion.empty(this.repository)
        case PromptType.interactiveElement:
          return PostRoomStatePromptVersion.empty(this.repository)
      }
    }
    const found = this.versions.models.find(
      (version) => version.id === this.versionId
    )
    if (!found) throw new Error(`Version with id ${this.versionId} not found`)

    return found
  }

  @computed
  /**
   * sorted list of history (version created, version published etc)
   */
  get versionLog() {
    type Action =
      | 'Version Created'
      | 'Version Published'
      | 'Version Installed'
      | 'Version Uninstalled'
    const versionLogs: Array<{
      action: Action
      note: string
      date?: Date
      userId?: string
    }> = []

    // push creation
    versionLogs.push({
      action: 'Version Created',
      note: this.version.data.versionNotes ?? '-',
      date: this.version.data.createdAt,
      userId: this.version.data.creatorUserId,
    })

    // push published
    if (this.version.data.versionStatus > PromptVersionStatus.draft) {
      versionLogs.push({
        action: 'Version Published',
        note: this.version.data.publisherNotes ?? '-',
        date: this.version.data.publishedDate,
        userId: this.version.data.publisherUserId,
      })
    }

    // iterate over the version oldest to newest (reverse the desc order)
    for (let i = this.currentVersions.models.length - 1; i >= 0; i--) {
      const observingCurrentVersion = this.currentVersions.models[i]
      if (observingCurrentVersion.data.promptVersionId !== this.versionId)
        continue

      // push the install record
      versionLogs.push({
        action: 'Version Installed',
        note: observingCurrentVersion.data.installNotes ?? '-',
        date: observingCurrentVersion.data.updatedAt,
        userId: observingCurrentVersion.data.userId,
      })

      // push uninstall record if there is a next version
      if (i - 1 >= 0) {
        const nextCurrentVersion = this.currentVersions.models[i - 1]
        const promptVersionFromNextId = this.versions.models.find(
          (version) => version.id === nextCurrentVersion.data.promptVersionId
        )

        if (nextCurrentVersion.data.promptVersionId === this.versionId)
          throw new Error('Duplicate installation of the same version')

        versionLogs.push({
          action: 'Version Uninstalled',
          note: promptVersionFromNextId
            ? `Replaced with Version '${promptVersionFromNextId.data.versionName}'`
            : '-',
          date: nextCurrentVersion.data.updatedAt,
          userId: nextCurrentVersion.data.userId,
        })
      }
    }
    return versionLogs
  }
}

class RoomNotCompletedError extends Error {
  constructor(roomId: string) {
    super(`Room ${roomId} not completed`)
    this.name = 'RoomNotCompletedError'
  }
}

type StreamResult<T> = T extends StreamInterface<infer U> ? U : never
